// Turns raw EQL rows (POST /reports/eql/ without `aggregate`) into flat
// records keyed by dimension, ready for computePivot().

import { transformDayJs } from "../../../../helpers/dateTransform";
import { DASH, applyDepth, prettyCatalogValue } from "./dimensions";
import { RECORD_ID_FIELD, durationsOfRow } from "./measures";

// EQL returns indicator booleans as real booleans (raw rows) or "True"/"False"
// strings (aggregate). Treat both, plus loose truthy strings, uniformly.
export const isTruthyFlag = (v) =>
  v === true || v === "True" || v === "true" || v === 1;

/**
 * Values a single dimension contributes for one row. Always an array: a
 * metadata catalog value may be a list (multi-select), and the backend counts
 * each element separately in aggregate mode — we mirror that here.
 */
const valuesFor = (row, dim, labels) => {
  const raw = row?.[dim.responseKey];

  if (dim.isCatalog) {
    if (raw == null || raw === "") return [DASH];
    const list = Array.isArray(raw) ? raw : [raw];
    if (!list.length) return [DASH];
    // Depth is per-catalog: each catalog dimension carries its own `depth`.
    // labels.catalogLabel localizes the value keys; prettyCatalogValue is the
    // fallback (e.g. tests or before the option locales have loaded).
    const label = labels?.catalogLabel
      ? (v) => labels.catalogLabel(dim, v)
      : prettyCatalogValue;
    return list.map((v) => label(applyDepth(v, dim.depth)));
  }

  if (dim.isTagFlag) {
    // "Has this tag?" — membership in the report's tag array.
    const arr = Array.isArray(raw) ? raw : [];
    return [arr.includes(dim.tagKey) ? labels.yes : labels.no];
  }

  if (dim.isIndicator) {
    // Boolean flag: absent -> "—", otherwise Yes/No. Both modes agree
    // (aggregate leaves null as EMPTY, mapping true/false via aggregateLabelOf).
    if (raw == null) return [DASH];
    return [isTruthyFlag(raw) ? labels.yes : labels.no];
  }

  if (dim.derive === "year" || dim.derive === "month") {
    const d = transformDayJs(raw);
    if (!d) return [DASH];
    return dim.derive === "year"
      ? [String(d.year())]
      : [labels.monthLabel(String(d.month() + 1).padStart(2, "0"))];
  }

  // closed_type owns its empty bucket ("en proceso de cierre"), so it must run
  // before the generic null->dash guard below.
  if (dim.key === "closed_type") return [labels.closedTypeLabel(raw)];

  if (raw == null || raw === "") return [DASH];

  switch (dim.key) {
    case "status":
      return [labels.statusLabel(raw)];
    case "entryway":
      return [labels.entrywayLabel(raw)];
    case "severity":
      return [labels.severityLabel(raw)];
    default:
      return [String(raw)];
  }
};

/** Cartesian product of per-dimension value lists. */
const expand = (dims, valueLists) => {
  let combos = [{}];
  dims.forEach((dim, i) => {
    const values = valueLists[i];
    const next = [];
    combos.forEach((combo) => {
      values.forEach((v) => next.push({ ...combo, [dim.key]: v }));
    });
    combos = next;
  });
  return combos;
};

/**
 * @param {Array<Object>} rows   raw EQL rows
 * @param {Array<Object>} dims   dimensions requested (rows+cols+filters); each
 *   catalog dim carries its own `depth`
 * @param {Object} opts          { labels, withDurations }
 *   withDurations: also attach the report id and every status duration
 *   (see measures.js), for a pivot whose cells summarize times, not counts.
 * @returns {Array<Object>} records, one per row (or per combination when a
 *   catalog dimension holds a list)
 */
export const flattenRows = (rows, dims, { labels, withDurations = false } = {}) => {
  if (!dims?.length) return [];
  const records = [];
  (rows || []).forEach((row, i) => {
    const valueLists = dims.map((d) => valuesFor(row, d, labels));
    const combos = expand(dims, valueLists);
    if (withDurations) {
      const extra = { [RECORD_ID_FIELD]: row?.id ?? i, ...durationsOfRow(row) };
      combos.forEach((c) => Object.assign(c, extra));
    }
    records.push(...combos);
  });
  return records;
};
